import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, Users, Briefcase, ArrowRight, Search, SlidersHorizontal, X, Building2, Globe, Wifi, Clock } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import api from "../lib/api";
import { PROFESSIONS, ENGAGEMENT_TYPES, COMPENSATION_TYPES, localize, languageLabel, formatDateRange } from "../lib/meta";
import { useLanguageOptions, useLanguageLabeler } from "../lib/taxonomy";
import { EmptyState } from "../components/EmptyState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";

const ANY = "any";
const emptyF = {
  q: "", required_profession: ANY, required_language: ANY, location: "", modality: ANY,
  engagement: ANY, compensation: ANY, verified_required: false, min_exp: ANY,
  crisis: false, group_work: false, training: false, organizational: false,
  deadline_before: "", open_only: false,
};

function OpportunityCard({ i }) {
  const { t, lang } = useLang();
  const statusInfo = i.status === "closed"
    ? { label: t("opportunities.closed_status"), cls: "bg-secondary text-slateink/60" }
    : i.deadline_passed
      ? { label: t("opportunities.deadline_passed"), cls: "bg-secondary text-slateink/60" }
      : i.is_full
        ? { label: t("opportunities.full_status"), cls: "bg-[#F5F3EF] text-[#5A544B]" }
        : { label: t("opportunities.open_status"), cls: "bg-[#EBF6F0] text-[#1E6B47]" };
  const modality = (i.online_required && i.in_person_required) ? t("initiatives.online_and_in_person")
    : i.online_required ? t("common.online") : i.in_person_required ? t("common.in_person") : null;
  const deadline = i.application_deadline ? formatDateRange(i.application_deadline, null, lang) : null;

  return (
    <Link to={`/oportunidades/${i.id}`} data-testid={`opportunity-card-${i.id}`}
      className="group flex flex-col rounded-2xl border border-border bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-terracotta/40 hover:shadow-[0_8px_30px_rgba(200,109,81,0.08)]">
      <div className="flex items-start justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-terracotta-light px-2.5 py-1 text-[11px] font-semibold text-terracotta"><Briefcase className="h-3 w-3" />{t("opportunities.badge")}</span>
        <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${statusInfo.cls}`} data-testid={`opp-status-${i.id}`}>{statusInfo.label}</span>
      </div>
      <h3 className="mt-3 font-display text-xl font-semibold tracking-tight text-slateink">{i.name}</h3>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
        {i.organizer_name && <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{i.organizer_name}</span>}
        {i.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{i.location}</span>}
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {i.required_profession && <span className="rounded-full bg-slateink px-2 py-0.5 text-[11px] font-semibold text-white">{localize(PROFESSIONS, i.required_profession, lang)}</span>}
        {i.engagement_type && <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-secondary-foreground">{localize(ENGAGEMENT_TYPES, i.engagement_type, lang)}</span>}
        {i.compensation_type && <span className="rounded-full bg-[#EFEFFD] px-2 py-0.5 text-[11px] font-medium text-[#3D3893]">{localize(COMPENSATION_TYPES, i.compensation_type, lang)}</span>}
        {i.required_credential_status === "verified" && <span className="rounded-full bg-[#EBF6F0] px-2 py-0.5 text-[11px] font-medium text-[#1E6B47]">{t("opportunities.verified_required")}</span>}
      </div>

      <div className="mt-4 space-y-1 text-xs text-muted-foreground">
        {i.required_languages?.length > 0 && <p className="inline-flex items-center gap-1"><Globe className="h-3.5 w-3.5" />{i.required_languages.map((l) => languageLabel(l, lang)).join(" · ")}</p>}
        {modality && <p className="inline-flex items-center gap-1"><Wifi className="h-3.5 w-3.5" />{modality}</p>}
        {i.min_experience != null && <p>{t("opportunities.min_experience")}: {i.min_experience} {t("opportunities.years")}</p>}
        {i.professionals_needed != null && <p className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{t("opportunities.professionals_needed")}: {i.professionals_needed}</p>}
        {deadline && <p className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{t("opportunities.deadline_label")}: {deadline}</p>}
      </div>

      <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-terracotta">{t("opportunities.view_details")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
    </Link>
  );
}

export default function Opportunities() {
  const { t, lang } = useLang();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState(emptyF);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [adv, setAdv] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = useCallback(() => {
    setLoading(true);
    const params = { opportunities_only: true };
    if (f.q) params.q = f.q;
    if (f.required_profession !== ANY) params.required_profession = f.required_profession;
    if (f.required_language !== ANY) params.required_language = f.required_language;
    if (f.location) params.location = f.location;
    if (f.modality === "online") params.online = true;
    else if (f.modality === "in_person") params.in_person = true;
    else if (f.modality === "hybrid") { params.online = true; params.in_person = true; }
    if (f.engagement !== ANY) params.engagement = f.engagement;
    if (f.compensation !== ANY) params.compensation = f.compensation;
    if (f.verified_required) params.verified_required = true;
    if (f.min_exp !== ANY) params.min_exp = f.min_exp;
    if (f.crisis) params.crisis = true;
    if (f.group_work) params.group_work = true;
    if (f.training) params.training = true;
    if (f.organizational) params.organizational = true;
    if (f.deadline_before) params.deadline_before = f.deadline_before;
    if (f.open_only) params.open_only = true;
    api.get("/initiatives", { params }).then((r) => setList(r.data)).catch(() => setList([])).finally(() => setLoading(false));
  }, [f]);

  useEffect(() => { const id = setTimeout(load, 300); return () => clearTimeout(id); }, [load]);

  const hasFilters = f.q || f.required_profession !== ANY || f.required_language !== ANY || f.location || f.modality !== ANY
    || f.engagement !== ANY || f.compensation !== ANY || f.verified_required || f.min_exp !== ANY
    || f.crisis || f.group_work || f.training || f.organizational || f.deadline_before || f.open_only;
  const reset = () => setF(emptyF);
  const langOptions = useLanguageOptions();
  const langLabel = useLanguageLabeler();

  const FiltersPanel = (scope = "") => (
    <div className="space-y-5" data-testid={`opportunities-filters${scope}`}>
      <div>
        <Label>{t("opportunities.f_profession")}</Label>
        <Select value={f.required_profession} onValueChange={(v) => set("required_profession", v)}>
          <SelectTrigger className="mt-1.5" data-testid={`opp-filter-profession${scope}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ANY}>{t("common.all")}</SelectItem>{PROFESSIONS.map((p) => <SelectItem key={p.value} value={p.value}>{localize(PROFESSIONS, p.value, lang)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>{t("opportunities.f_language")}</Label>
        <Select value={f.required_language} onValueChange={(v) => set("required_language", v)}>
          <SelectTrigger className="mt-1.5" data-testid={`opp-filter-language${scope}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ANY}>{t("common.all")}</SelectItem>{langOptions.map((l) => <SelectItem key={l.value} value={l.value}>{langLabel(l.value, lang)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>{t("opportunities.f_location")}</Label>
        <Input className="mt-1.5" value={f.location} onChange={(e) => set("location", e.target.value)} data-testid={`opp-filter-location${scope}`} />
      </div>
      <div>
        <Label>{t("opportunities.f_modality")}</Label>
        <Select value={f.modality} onValueChange={(v) => set("modality", v)}>
          <SelectTrigger className="mt-1.5" data-testid={`opp-filter-modality${scope}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{t("common.all")}</SelectItem>
            <SelectItem value="online">{t("common.online")}</SelectItem>
            <SelectItem value="in_person">{t("common.in_person")}</SelectItem>
            <SelectItem value="hybrid">{t("initiatives.online_and_in_person")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>{t("opportunities.f_engagement")}</Label>
        <Select value={f.engagement} onValueChange={(v) => set("engagement", v)}>
          <SelectTrigger className="mt-1.5" data-testid={`opp-filter-engagement${scope}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ANY}>{t("common.all")}</SelectItem>{ENGAGEMENT_TYPES.map((e) => <SelectItem key={e.value} value={e.value}>{localize(ENGAGEMENT_TYPES, e.value, lang)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>{t("opportunities.f_compensation")}</Label>
        <Select value={f.compensation} onValueChange={(v) => set("compensation", v)}>
          <SelectTrigger className="mt-1.5" data-testid={`opp-filter-compensation${scope}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ANY}>{t("common.all")}</SelectItem>{COMPENSATION_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{localize(COMPENSATION_TYPES, c.value, lang)}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div>
        <button type="button" onClick={() => setAdv((s) => !s)} data-testid={`opp-toggle-advanced${scope}`} className="flex w-full items-center justify-between border-t border-border pt-4 text-sm font-semibold text-slateink">
          {t("opportunities.advanced")}<span className="text-terracotta">{adv ? "−" : "+"}</span>
        </button>
      </div>
      {adv && (<>
        <div>
          <Label>{t("opportunities.f_experience")}</Label>
          <Select value={f.min_exp} onValueChange={(v) => set("min_exp", v)}>
            <SelectTrigger className="mt-1.5" data-testid={`opp-filter-experience${scope}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>{t("common.all")}</SelectItem>
              {["2", "5", "10"].map((y) => <SelectItem key={y} value={y}>≤ {y} {t("opportunities.years")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("opportunities.f_deadline_before")}</Label>
          <Input type="date" className="mt-1.5" value={f.deadline_before} onChange={(e) => set("deadline_before", e.target.value)} data-testid={`opp-filter-deadline${scope}`} />
        </div>
        <div className="space-y-3">
          {[["verified_required", t("opportunities.f_verified_required")], ["crisis", t("opportunities.f_crisis")], ["group_work", t("opportunities.f_group")], ["training", t("opportunities.f_training")], ["organizational", t("opportunities.f_organizational")]].map(([k, label]) => (
            <label key={k} className="flex cursor-pointer items-center gap-2.5 text-sm text-slateink/80">
              <Checkbox checked={f[k]} onCheckedChange={(v) => set(k, !!v)} data-testid={`opp-filter-${k}${scope}`} />{label}
            </label>
          ))}
        </div>
      </>)}

      <div className="border-t border-border pt-4">
        <label className="flex cursor-pointer items-center gap-2.5 text-sm text-slateink/80">
          <Checkbox checked={f.open_only} onCheckedChange={(v) => set("open_only", !!v)} data-testid={`opp-filter-open_only${scope}`} />{t("opportunities.f_open_only")}
        </label>
      </div>
      {hasFilters && (
        <Button variant="outline" className="w-full rounded-full" onClick={reset} data-testid={`opp-clear-filters${scope}`}>
          <X className="mr-1.5 h-4 w-4" />{t("opportunities.clear_filters")}
        </Button>
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <div className="flex items-center gap-2 text-terracotta"><Briefcase className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-widest">{t("opportunities.badge")}</span></div>
        <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-slateink sm:text-4xl">{t("opportunities.title")}</h1>
        <p className="mt-3 text-muted-foreground">{t("opportunities.subtitle")}</p>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder={t("opportunities.search_placeholder")} value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="opportunities-search" />
        </div>
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="rounded-full lg:hidden" data-testid="opp-open-filters">
              <SlidersHorizontal className="mr-1.5 h-4 w-4" />{t("opportunities.filters")}
              {hasFilters && <span className="ml-1.5 h-2 w-2 rounded-full bg-terracotta" />}
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto" data-testid="opp-filters-sheet">
            <SheetHeader><SheetTitle>{t("opportunities.filters")}</SheetTitle></SheetHeader>
            <div className="mt-6">{FiltersPanel("-m")}</div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="mt-8 lg:grid lg:grid-cols-[280px_1fr] lg:gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto rounded-2xl border border-border bg-white p-6">
            <p className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground"><SlidersHorizontal className="h-4 w-4" />{t("opportunities.filters")}</p>
            {FiltersPanel("")}
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="grid gap-6 md:grid-cols-2">{[0, 1].map((k) => <div key={k} className="h-64 animate-pulse rounded-2xl bg-white/60" />)}</div>
          ) : list.length === 0 ? (
            hasFilters
              ? <EmptyState icon={Search} title={t("opportunities.no_match_title")} description={t("opportunities.no_match_desc")}
                  action={<Button variant="outline" className="rounded-full" onClick={reset} data-testid="opp-empty-clear">{t("opportunities.clear_filters")}</Button>} testId="opportunities-no-match" />
              : <EmptyState icon={Briefcase} title={t("opportunities.empty_title")} description={t("opportunities.empty_desc")} testId="opportunities-empty" />
          ) : (
            <>
              <p className="mb-5 text-sm text-muted-foreground" data-testid="opp-results-count">
                {list.length} {list.length === 1 ? t("opportunities.result") : t("opportunities.results")}
              </p>
              <div className="grid gap-6 md:grid-cols-2">{list.map((i) => <OpportunityCard key={i.id} i={i} />)}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
